import {
  BottomSheetBackdrop,
  type BottomSheetBackdropProps,
  BottomSheetModal,
  type BottomSheetProps,
  BottomSheetScrollView,
  BottomSheetView,
} from '@gorhom/bottom-sheet'
import clsx from 'clsx'
import { BlurView } from 'expo-blur'
import { type ComponentType, useEffect, useRef, type ReactElement, type ReactNode } from 'react'
import { Dimensions, Platform, View } from 'react-native'
import { Touchable } from 'react-native-gesture-handler'
import Animated, { Extrapolation, interpolate, useAnimatedStyle, useSharedValue } from 'react-native-reanimated'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { FullWindowOverlay } from 'react-native-screens'

import { Typography } from '@/src/elements/text/Typography'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { useDimensions } from '@/src/general/hooks/useDimensions'
import { usePrevious } from '@/src/general/hooks/usePrevious'
import { useTranslate } from '@/src/lang/useTranslate'
import type { SlotsToClasses } from '@/src/types/slot-to-classes'

import { Button, type TDefaultButtonProps } from '../button/default'
import { Card, CardBody, CardFooter, CardHeader } from '../card'
import { modalVariants, type TModalSlots, type TModalVariantProps } from './modal-variants'

export type TModalProps = {
  testID?: string
  colors?: 'primary' | 'danger'
  title?: ReturnType<ReturnType<typeof useTranslate>['t']> | string | ReactElement
  description?: string
  showHandle?: boolean
  showCancelButton?: boolean
  confirmButtonProps?: TDefaultButtonProps
  cancelButtonProps?: TDefaultButtonProps
  hideCloseButton?: boolean
  onCancel?: VoidFunction
  onConfirm?: VoidFunction
  children?: ReactNode
  cancelText?: string
  confirmText?: string
  showModal: boolean
  classNames?: SlotsToClasses<TModalSlots>
  fullWidth?: 'mobile' | true
  maxDynamicContentSize?: number
  scrollable?: boolean
  blurBackdrop?: boolean
} & Omit<TModalVariantProps, 'fullWidth'> &
  Pick<BottomSheetProps, 'style' | 'backgroundStyle' | 'containerStyle'>

const AnimatedBlurView = Animated.createAnimatedComponent(BlurView)

const BlurBackdropAnimatedContainer = ({
  animatedIndex,
  style,
  onClose,
}: BottomSheetBackdropProps & { onClose: VoidFunction }) => {
  const containerAnimatedStyle = useAnimatedStyle(() => ({
    opacity: interpolate(animatedIndex.value, [-1, 0], [0, 1]),
  }))

  return (
    <Touchable onPress={onClose}>
      <AnimatedBlurView testID={TEST_ID.MODAL.BACKDROP} style={[style, containerAnimatedStyle]} />
    </Touchable>
  )
}

export const Modal = ({
  testID,
  colors = 'primary',
  title,
  description,
  showCancelButton = false,
  confirmButtonProps,
  cancelButtonProps,
  hideCloseButton = false,
  onCancel,
  onConfirm,
  children,
  cancelText,
  confirmText,
  classNames,
  showModal,
  fullWidth,
  scrollable,
  maxDynamicContentSize,
  style,
  blurBackdrop,
  backgroundStyle,
  containerStyle,
  showHandle = true,
}: TModalProps): ReactNode => {
  const refButton = useRef<View>(null)
  const bottomSheetRef = useRef<BottomSheetModal>(null)
  const hasConsumedCancelButton = useRef(false)
  const hasConsumedConfirmButton = useRef(false)

  const animatedPosition = useSharedValue(0)
  const animatedIndex = useSharedValue(-1)

  const previousShowModal = usePrevious(showModal)
  const { lessThan } = useDimensions()
  const { t } = useTranslate()
  const { bottom } = useSafeAreaInsets()

  const shouldShowHandle = lessThan.md && showHandle

  const isDetached = !lessThan.md

  const slots = modalVariants({
    phoneViewport: lessThan.md,
    shadow: 'xl',
    radius: 'xl',
    fullWidth: fullWidth === 'mobile' ? lessThan.md : fullWidth,
  })
  const centeredStyle = useAnimatedStyle(() => {
    if (!isDetached) {
      return { marginTop: 0 }
    }

    // No offset when the sheet is closed; full offset when it is open.
    const openingProgress = interpolate(animatedIndex.value, [-1, 0], [0, 1], Extrapolation.CLAMP)

    return {
      marginTop: -(animatedPosition.value / 2) * openingProgress,
    }
  }, [isDetached])

  useEffect(() => {
    if (refButton?.current?.blur) refButton.current.blur()
  }, [refButton])

  useEffect(() => {
    if (showModal && !previousShowModal) {
      hasConsumedCancelButton.current = false
      hasConsumedConfirmButton.current = false
      bottomSheetRef.current?.present()
    }
    if (!showModal && previousShowModal) {
      hasConsumedCancelButton.current = true
      bottomSheetRef.current?.close?.()
    }
  }, [showModal, previousShowModal])

  const WrapperComponent = scrollable ? BottomSheetScrollView : BottomSheetView

  const handleCancel = () => {
    hasConsumedCancelButton.current = true
    onCancel?.()
  }

  const handleConfirm = () => {
    hasConsumedConfirmButton.current = true
    onConfirm?.()
  }

  return (
    <BottomSheetModal
      enableDismissOnClose
      containerComponent={
        Platform.OS === 'ios'
          ? // oxlint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- gesture-handler typing
            (FullWindowOverlay as ComponentType<{ children?: ReactNode }>)
          : undefined
      }
      enablePanDownToClose
      enableHandlePanningGesture
      backdropComponent={(backdropProps) =>
        blurBackdrop ? (
          <BlurBackdropAnimatedContainer {...backdropProps} onClose={handleCancel} />
        ) : (
          <BottomSheetBackdrop
            appearsOnIndex={0}
            disappearsOnIndex={-1}
            opacity={0.5}
            pressBehavior="close"
            {...backdropProps}
          />
        )
      }
      handleComponent={
        shouldShowHandle
          ? (props) => (
              <Touchable className={slots.handleContainer({ className: classNames?.handleContainer })} {...props}>
                <View testID={TEST_ID.MODAL.HANDLE} className={slots.handle({ className: classNames?.handle })} />
              </Touchable>
            )
          : null
      }
      maxDynamicContentSize={maxDynamicContentSize || Dimensions.get('window').height * 0.9}
      onDismiss={() => {
        if (!hasConsumedCancelButton.current && !hasConsumedConfirmButton.current) {
          onCancel?.()
        }
      }}
      ref={bottomSheetRef}
      detached={isDetached}
      containerStyle={
        !lessThan.md
          ? {
              marginLeft: 'auto',
              marginRight: 'auto',
              maxWidth: fullWidth ? '90%' : 450,
              ...containerStyle,
            }
          : null
      }
      style={[
        {
          overflow: 'hidden',
          borderRadius: 16,
          shadowColor: 'black',
          shadowOffset: { width: 3, height: 0 },
          shadowOpacity: 0.1,
          shadowRadius: 15,
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
        },
        style,
        isDetached ? centeredStyle : null,
      ]}
      animatedPosition={animatedPosition}
      animatedIndex={animatedIndex}
      keyboardBehavior="interactive"
      backgroundStyle={backgroundStyle}
    >
      <WrapperComponent
        testID={testID ?? TEST_ID.MODAL.WRAPPER}
        style={{
          flex: 1,
          paddingBottom: bottom,
          width: '100%',
        }}
      >
        <Card className={slots.base({ className: clsx(classNames?.base, 'rounded-none self-center'), shadow: 'none' })}>
          <CardHeader className={slots.header({ className: classNames?.header })}>
            {!hideCloseButton && !shouldShowHandle && (
              <View className={slots.closeButton()}>
                <Button
                  isIconOnly
                  startIcon={ICON.CLOSE}
                  colors="default"
                  variant="light"
                  contrast="low"
                  testID={TEST_ID.MODAL.CLOSE_BUTTON}
                  ref={refButton}
                  onPress={handleCancel}
                  size={'md'}
                />
              </View>
            )}
            {title != null && (
              <Typography size="lg" bold className={slots.title({ className: classNames?.title })}>
                {title}
              </Typography>
            )}
          </CardHeader>
          <CardBody className={slots.body({ className: classNames?.body })}>
            {description != null && (
              <Typography className={slots.description({ className: classNames?.description })}>
                {description}
              </Typography>
            )}
            {children}
          </CardBody>
          {(!!showCancelButton || !!onConfirm) && (
            <CardFooter className={slots.footer({ className: classNames?.footer })}>
              {!!showCancelButton && (
                <Button onPress={handleCancel} colors="default" variant="light" {...cancelButtonProps}>
                  {cancelText || t('word.cancel')}
                </Button>
              )}
              {!!onConfirm && (
                <Button
                  onPress={handleConfirm}
                  variant="solid"
                  colors={colors}
                  testID={TEST_ID.MODAL.CONFIRM_BUTTON}
                  {...confirmButtonProps}
                >
                  {confirmText || t('word.validate')}
                </Button>
              )}
            </CardFooter>
          )}
        </Card>
      </WrapperComponent>
    </BottomSheetModal>
  )
}
